/**
 * Serviços de chat / conversa com médicos atendentes.
 */

import { apiClient, type ApiResponse } from './apiClient';
import { API_ENDPOINTS } from '@/constants/api';
import { normalizeConnectionsPayload } from '@/lib/normalizeConnectionsPayload';

export type ConversationAvailability =
  | 'available'
  | 'doctor_inactive'
  | 'doctor_unapproved'
  | 'link_revoked'
  | 'discharged'
  | 'no_thread';

export interface AttendedDoctor {
  id: string;
  patientId: string;
  doctorId: string;
  firstEncounterDate: string;
  lastEncounterDate: string;
  source: 'on-demand' | 'scheduled';
  status: 'active' | 'revoked';
  patientStatus: 'ACTIVE' | 'FOLLOW_UP' | 'DISCHARGED';
  lastAppointmentId: string | null;
  conversationAvailability: ConversationAvailability;
  conversationBlockedReason: string | null;
  doctor: {
    id: string;
    name: string;
    email: string;
    profileId?: string;
    specialty?: string;
    crm?: string;
    crmState?: string;
    isApproved?: boolean;
    isActive?: boolean;
    isAvailable?: boolean;
  } | null;
}

export interface StartConversationResult {
  appointmentId: string;
  doctorId: string;
  patientId: string;
  created: boolean;
  reused: boolean;
}

export async function getAttendedDoctors(): Promise<ApiResponse<AttendedDoctor[]>> {
  const result = await apiClient.get<AttendedDoctor[] | { data: AttendedDoctor[] }>(
    API_ENDPOINTS.MEDICAL_RECORDS.ATTENDED_DOCTORS,
  );

  if (!result.success) {
    return {
      success: false,
      error: result.error || 'Erro ao carregar médicos atendentes',
      message: result.message,
    };
  }

  return {
    success: true,
    data: normalizeConnectionsPayload<AttendedDoctor>(result.data),
  };
}

export async function startConversationWithDoctor(
  doctorId: string,
): Promise<ApiResponse<StartConversationResult>> {
  return apiClient.post<StartConversationResult>(
    API_ENDPOINTS.CHAT.START_CONVERSATION,
    { doctorId },
  );
}

/** Médico abre/reutiliza thread de chat com um paciente vinculado. */
export async function startConversationWithPatient(
  patientId: string,
): Promise<ApiResponse<StartConversationResult>> {
  return apiClient.post<StartConversationResult>(
    API_ENDPOINTS.CHAT.START_CONVERSATION,
    { patientId },
  );
}
